import { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';

import {
  ErpFundingImportRunsDocument,
  ErpFundingImportRunsQuery,
  ErpFundingImportRunsQueryVariables,
  ErpFundingNotionNamesDocument,
  ErpFundingNotionNamesQuery,
  ErpFundingNotionNamesQueryVariables,
  useErpFundingNotionSyncQuery,
  useErpRunFundingTaskMutation,
} from 'core/graphql';

import { usePageVisiblePoll } from '../Meta/usePageVisiblePoll';
import {
  IFundingTaskRun,
  NOTION_SYNC_TASK,
  NOTION_SYNC_TASK_NAME,
  notionPreviewRun,
  notionReport,
} from './dofinansowanieModel';
import { useDofinPermissions } from './useDofinPermissions';

/**
 * ТЗ 0087 — синхронизация дофинансирования из Notion на время перехода.
 *
 * Прогон уходит в Celery: кнопка ставит задачу в очередь, а результат
 * появляется строкой журнала. Поэтому состояние опрашивается: часто — пока
 * прогон идёт или пока ждём прогон, поставленный кнопкой (первые секунды его
 * в журнале ещё нет); редко — всё остальное время, иначе ежечасный прогон
 * на открытой странице не виден до перезагрузки. Своего ждём не дольше
 * `NOTION_SYNC_WATCH_MS`: упавший воркер не должен держать «в очереди»
 * вечно — по таймауту экран говорит, что задачу никто не взял.
 */
export const NOTION_SYNC_POLL_MS = 5000;
export const NOTION_SYNC_IDLE_POLL_MS = 60 * 1000;
export const NOTION_SYNC_WATCH_MS = 5 * 60 * 1000;

export interface INotionSyncState {
  keyConfigured: boolean;
  liveBlocked: boolean;
  autoEnabled: boolean;
  isRunning: boolean;
  robotMailFlags: string[];
}

export interface INotionWatchOptions {
  /** Сервер поставил задачу, но воркер не ответил на пинг. */
  noWorker?: boolean;
}

export interface INotionSyncStatus {
  /** `null` — ещё не загрузилось (или нет прав на раздел). */
  state: INotionSyncState | null;
  /** Последний прогон — любой: сухой, упавший, пропущенный. */
  lastRun: IFundingTaskRun | null;
  /** Последняя НАСТОЯЩАЯ синхронизация: «когда данные забраны». */
  lastSuccess: IFundingTaskRun | null;
  /**
   * Прогон идёт прямо сейчас. 🔴 Единственный источник — замок сервера
   * (`isRunning`): строка журнала `running` бывает и у оборванного прогона.
   */
  running: boolean;
  /** Поставлен кнопкой и ещё не начался. */
  waiting: boolean;
  /** Поставлен, но воркер не ответил: ждать, скорее всего, нечего. */
  noWorker: boolean;
  /** Ждали своего прогона `NOTION_SYNC_WATCH_MS` — никто его не взял. */
  stalled: boolean;
  /** Строка журнала «выполняется», а замка нет: прогон оборвался. */
  abandoned: boolean;
  /**
   * Id прогона, который приземлился при открытой странице и НИЧЕГО не
   * записал (проверка, пропуск, отказ). Записавший перечитывает весь экран
   * сам; этому достаточно перечитать журнал.
   */
  landedWithoutWriteId: string | null;
  loading: boolean;
  failed: boolean;
  reload: () => void;
  /** Начать ждать прогон, который только что поставили в очередь. */
  watchNextRun: (options?: INotionWatchOptions) => void;
}

interface IWatch {
  since: string | null;
  noWorker: boolean;
}

export const useNotionSyncStatus = (): INotionSyncStatus => {
  const { canRead } = useDofinPermissions();
  const client = useApolloClient();
  const skip = !canRead;
  const {
    data,
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  } = useErpFundingNotionSyncQuery({
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const raw = data?.fundingNotionSync ?? null;
  const lastRun = (raw?.lastRun ?? null) as IFundingTaskRun | null;
  const lastSuccess = (raw?.lastSuccess ?? null) as IFundingTaskRun | null;
  const running = Boolean(raw?.isRunning);
  const lastRunId = lastRun?.id ?? null;

  // Прогон после нажатия — это ДРУГОЙ id, чем был в момент нажатия.
  const [watch, setWatch] = useState<IWatch | null>(null);
  const [stalledSince, setStalledSince] = useState<
    { since: string | null } | undefined
  >(undefined);
  const [landedWithoutWriteId, setLandedWithoutWriteId] = useState<
    string | null
  >(null);
  const lastIdRef = useRef<string | null>(null);
  lastIdRef.current = lastRunId;
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  const ownRunStarted = Boolean(watch && lastRun && lastRunId !== watch.since);
  const settled = ownRunStarted && lastRun?.status !== 'running';

  useEffect(() => {
    if (settled) setWatch(null);
  }, [settled]);

  useEffect(() => {
    if (!watch) return undefined;
    const timer = window.setTimeout(() => {
      setWatch(null);
      setStalledSince({ since: watch.since });
    }, NOTION_SYNC_WATCH_MS);
    return () => window.clearTimeout(timer);
  }, [watch]);

  // Пропущенный прогон нашёлся — «никто не взял» больше не правда.
  const stalled = Boolean(
    stalledSince && stalledSince.since === lastRunId && !running,
  );
  // Своя свежая строка `running` до того, как прогон взял замок, — это
  // «в очереди», а не обрыв.
  const abandoned = lastRun?.status === 'running' && !running && !ownRunStarted;

  usePageVisiblePoll(
    startPolling,
    stopPolling,
    watch || running ? NOTION_SYNC_POLL_MS : NOTION_SYNC_IDLE_POLL_MS,
    !skip,
  );

  /**
   * 🔴 Записавшая синхронизация меняет ВСЁ, что на экране: кандидатов,
   * операторов, связи, окна набора, доску и очереди. Пока их не перечитали,
   * экран показывает данные до синхронизации — и человек правит строку,
   * которую Notion только что переписал.
   *
   * Перечитываем все АКТИВНЫЕ запросы (`reFetchObservableQueries`), а не
   * по списку:
   * * `refetchQueries` по имени на Apollo 3.3 есть только у мутации, а
   *   мутация здесь давно закончилась — прогон приземляется через минуту;
   *   к тому же по имени бьётся и пропущенный (`skip`) запрос, отправляясь
   *   с пустыми переменными;
   * * вытеснение полей из кеша на 3.3 оставляло таблицу на старых строках
   *   (живая проверка 0080);
   * * `reFetchObservableQueries` берёт только запросы с подписчиками —
   *   пропущенный подписчиков не имеет и не трогается, — и каждый уходит со
   *   СВОИМИ переменными, то есть открытая страница и фильтры сохраняются.
   *   Постраничной догрузки (`fetchMore`) в разделе нет, терять нечего.
   *
   * Отслеживаем приземление ЛЮБОГО прогона, кончившегося при открытой
   * странице: ежечасная синхронизация или кнопка коллеги меняют данные так
   * же, как своя. ⚠️ Точка отсчёта — первый ответ СЕТИ, а не кеша: при
   * `cache-and-network` кеш отдаёт вчерашний прогон, сеть — сегодняшний, и
   * сравнение этих двух на загрузке перечитывало бы весь раздел зря.
   */
  const seenRef = useRef<string | null | undefined>(undefined);
  const landedId = lastRun && lastRun.status !== 'running' ? lastRun.id : null;
  const landedWrote = Boolean(
    lastRun &&
      !lastRun.dryRun &&
      lastRun.status === 'success' &&
      !notionReport(lastRun.stats).skipped,
  );
  const fromNetwork = Boolean(raw) && !loading;

  useEffect(() => {
    if (!fromNetwork) return;
    if (seenRef.current === undefined) {
      seenRef.current = landedId;
      return;
    }
    if (!landedId || landedId === seenRef.current) return;
    seenRef.current = landedId;
    if (landedWrote) {
      client.reFetchObservableQueries().catch(() => undefined);
    } else {
      setLandedWithoutWriteId(landedId);
    }
  }, [client, fromNetwork, landedId, landedWrote]);

  const reload = useCallback(() => {
    refetch()?.catch(() => undefined);
  }, [refetch]);

  const watchNextRun = useCallback((options?: INotionWatchOptions) => {
    // Прогон ставят из обработчика с `await`: страницу могли закрыть, пока
    // сервер отвечал.
    if (!mountedRef.current) return;
    setStalledSince(undefined);
    setWatch({
      since: lastIdRef.current,
      noWorker: Boolean(options?.noWorker),
    });
  }, []);

  const waiting = Boolean(watch) && !running;

  return {
    state: raw
      ? {
          keyConfigured: raw.keyConfigured,
          liveBlocked: raw.liveBlocked,
          autoEnabled: raw.autoEnabled,
          isRunning: raw.isRunning,
          robotMailFlags: raw.robotMailFlags,
        }
      : null,
    lastRun,
    lastSuccess,
    running,
    waiting,
    noWorker: waiting && Boolean(watch?.noWorker),
    stalled,
    abandoned,
    landedWithoutWriteId,
    loading: loading && !data,
    failed: Boolean(error) && !data,
    reload,
    watchNextRun,
  };
};

/**
 * Сухой прогон, по которому подтверждение записи называет число заменяемых
 * ручных правок.
 *
 * Обычно это и есть последний прогон — человек только что нажал
 * «Проверить». Но между проверкой и записью может встать упавший или
 * пропущенный прогон, и тогда проверку ищем в журнале: последние несколько
 * сухих прогонов этой задачи.
 *
 * ⚠️ Запросом В МОМЕНТ нажатия, а не подпиской на странице: ежечасная
 * синхронизация пишет, и последний прогон почти всегда — запись. Подписка
 * ходила бы за журналом на каждом открытии раздела ради кнопки, которую
 * жмут редко; а число из свежего запроса ещё и не отстаёт от журнала.
 * Отказ сети — `null`, то есть общее предупреждение, а не «ноль правок».
 */
export const useNotionPreviewFetcher = () => {
  const client = useApolloClient();

  return useCallback(
    async (
      lastRun: IFundingTaskRun | null,
      lastSuccess: IFundingTaskRun | null,
    ): Promise<IFundingTaskRun | null> => {
      const fromLast = notionPreviewRun([lastRun], lastSuccess);
      if (fromLast) return fromLast;
      try {
        const { data } = await client.query<
          ErpFundingImportRunsQuery,
          ErpFundingImportRunsQueryVariables
        >({
          query: ErpFundingImportRunsDocument,
          variables: {
            taskName: NOTION_SYNC_TASK_NAME,
            dryRun: true,
            status: 'success',
            limit: 5,
            offset: 0,
          },
          fetchPolicy: 'network-only',
        });
        return notionPreviewRun(
          (data?.fundingImportRuns ?? []) as IFundingTaskRun[],
          lastSuccess,
        );
      } catch {
        return null;
      }
    },
    [client],
  );
};

export type TNotionStartOutcome = 'queued' | 'no-worker' | 'refused' | 'failed';

export interface INotionStartResult {
  outcome: TNotionStartOutcome;
  /**
   * Код отказа (`already_running`, `no_notion_key`, `live_notion_blocked`,
   * `robot_mail_on`) — экран переводит его сам (`notionRefusalKey`).
   */
  code: string | null;
  /** Текст сервера (по-русски, для логов) — запасной для незнакомого кода. */
  message: string;
}

/**
 * Поставить синхронизацию в очередь.
 *
 * Сервер отказывает СРАЗУ (`customErrors`), когда: прогон уже идёт, на
 * сервере нет ключа, живой Notion этому окружению запрещён или — для
 * записи — включён робот рассылок ERP. Причина приходит КОДОМ: подменить её
 * общим «не удалось» значило бы спрятать, что чинить.
 */
export const useStartNotionSync = () => {
  const [mutate, state] = useErpRunFundingTaskMutation();

  const start = useCallback(
    async (dryRun: boolean): Promise<INotionStartResult> => {
      // try/catch обязателен: отказ прав летит в GraphQL `errors`, то есть
      // промис Apollo ОТКЛОНЯЕТСЯ.
      try {
        const { data } = await mutate({
          variables: { task: NOTION_SYNC_TASK, dryRun },
        });
        const payload = data?.runFundingTask;
        if (payload?.success !== true) {
          const errors = payload?.customErrors ?? [];
          const code = errors.find((item) => item?.code)?.code ?? null;
          const message = errors
            .map((item) => item?.message ?? '')
            .filter(Boolean)
            .join(' ');
          return {
            outcome: code || message ? 'refused' : 'failed',
            code,
            message,
          };
        }
        return {
          outcome: payload.workerOnline ? 'queued' : 'no-worker',
          code: null,
          message: '',
        };
      } catch {
        return { outcome: 'failed', code: null, message: '' };
      }
    },
    [mutate],
  );

  return { start, starting: state.loading };
};

/** Потолок страницы `fundingCandidates` / `fundingOperators` на сервере. */
const NAMES_PAGE = 200;

const chunks = (ids: string[]): string[][] => {
  const out: string[][] = [];
  for (let index = 0; index < ids.length; index += NAMES_PAGE) {
    out.push(ids.slice(index, index + NAMES_PAGE));
  }
  return out;
};

const keyOf = (ids: string[]): string =>
  Array.from(new Set(ids.filter(Boolean)))
    .sort()
    .join(',');

export interface IFundingNames {
  /** id → имя; `''` — строка есть, а имени у неё нет. */
  candidates: Record<string, string>;
  operators: Record<string, string>;
  /**
   * Ответ пришёл: id, которого в нём нет, удалён. Пока `false` (грузится
   * или сеть отказала) — отсутствие имени ничего не значит, показываем id.
   */
  resolved: boolean;
}

interface INamesState extends IFundingNames {
  key: string;
}

const NO_NAMES: IFundingNames = {
  candidates: {},
  operators: {},
  resolved: false,
};

/**
 * ТЗ 0087 — имена для списков «Кого это касается»: отчёт хранит только id.
 *
 * Списков пять, в каждом до 200 id, и в сумме они могут не влезть в одну
 * страницу сервера (200) — поэтому запрос режется на страницы, а не
 * молча теряет хвост. Пустой список не запрашивается: пустой `ids` сервер
 * понимает как «без фильтра».
 */
export const useFundingNames = (
  candidateIds: string[],
  operatorIds: string[],
): IFundingNames => {
  const client = useApolloClient();
  const candidateKey = keyOf(candidateIds);
  const operatorKey = keyOf(operatorIds);
  const key = `${candidateKey}|${operatorKey}`;
  const [state, setState] = useState<INamesState | null>(null);

  useEffect(() => {
    if (!candidateKey && !operatorKey) return undefined;
    let cancelled = false;
    const candidatePages = chunks(candidateKey ? candidateKey.split(',') : []);
    const operatorPages = chunks(operatorKey ? operatorKey.split(',') : []);
    const pages = Math.max(candidatePages.length, operatorPages.length);

    Promise.all(
      Array.from({ length: pages }, (_, index) => {
        const candidates = candidatePages[index] ?? [];
        const operators = operatorPages[index] ?? [];
        return client.query<
          ErpFundingNotionNamesQuery,
          ErpFundingNotionNamesQueryVariables
        >({
          query: ErpFundingNotionNamesDocument,
          variables: {
            candidateIds: candidates,
            operatorIds: operators,
            withCandidates: candidates.length > 0,
            withOperators: operators.length > 0,
            limit: NAMES_PAGE,
          },
          fetchPolicy: 'network-only',
        });
      }),
    )
      .then((results) => {
        if (cancelled) return;
        const next: INamesState = {
          key: `${candidateKey}|${operatorKey}`,
          candidates: {},
          operators: {},
          resolved: true,
        };
        results.forEach(({ data }) => {
          (data?.fundingCandidates ?? []).forEach((row) => {
            // Пустое имя — строка есть, имени нет: ключ всё равно ставим,
            // иначе её сочли бы удалённой.
            next.candidates[String(row.id)] = row.fullName ?? '';
          });
          (data?.fundingOperators ?? []).forEach((row) => {
            next.operators[String(row.id)] = row.name ?? '';
          });
        });
        setState(next);
      })
      .catch(() => {
        if (!cancelled) {
          setState({ ...NO_NAMES, key: `${candidateKey}|${operatorKey}` });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [client, candidateKey, operatorKey]);

  return state && state.key === key ? state : NO_NAMES;
};
